import type {
  AgentConnectionStatus,
  ChatAttachment,
  ChatMessage,
  ChatSession,
  CustomFolder,
  ModelInfo,
  RunningSubtasks,
  SessionContextUsage,
  SessionFolder,
  TerminalSyncStatus,
  UiRender,
} from '@paradigm/terminal-core';
import { NO_RUNNING_SUBTASKS } from '@paradigm/terminal-core';

import { createSafeContext } from '../context/createSafeContext';
import type { SidecarRequest } from '../sidecar/request';

/**
 * The in-flight indicator: one per run, alive for the WHOLE run rather than
 * only the wait before it.
 *
 * Everything a status line needs and nothing a frame can already tell you —
 * ChatView derives the rest (whether the answer has started, how much of it
 * has arrived) from the streaming message itself.
 */
export interface ProcessingState {
  readonly label: string;
  readonly detail?: string;
  /**
   * When this run started, stamped from the BROWSER clock (`Date.now()`),
   * not the gateway's `lifecycle.startedAt`. Two reasons: the gateway value
   * is a pod-side ms timestamp, so any clock skew would render as a wrong
   * (possibly negative) age; and local stamping also counts the wait before
   * the first frame lands, which is the part the user actually sits through.
   *
   * Set once when `processing` goes null -> non-null and carried forward
   * across label changes, so "Thinking" -> "Calling tool" does not restart
   * the clock mid-run. Content frames — answer deltas, widgets — no longer
   * clear the indicator, so there is nothing left to restart it against
   * mid-turn either.
   */
  readonly startedAt: number;
  /**
   * Characters of reasoning text streamed so far in this run, used to size
   * a live token estimate while the user waits.
   *
   * openclaw emits its `thinking` stream ONLY for models its catalog marks
   * reasoning-capable (`OPENCLAW_REASONING_PREFIXES` in
   * setup-openclaw-docker.sh). Everywhere else this stays 0 and the
   * indicator falls back to elapsed time alone, which is the correct
   * degradation: no signal, no number.
   *
   * The text itself is never kept — only its length. Reasoning content is
   * not rendered anywhere in this UI.
   */
  readonly thinkingChars: number;
  /**
   * The run this indicator belongs to, once a frame has named one.
   *
   * Undefined between "user pressed Send" and the first frame that carries a
   * runId — the client does not learn the id until the gateway answers.
   *
   * Two things depend on it. An end-of-run frame for a DIFFERENT run must not
   * tear this indicator down: a trailing `lifecycle.end` from the previous
   * turn resolves to this session and would otherwise clear the row for the
   * run that just started. And ChatView sizes its answer-character count from
   * the streaming message with this runId, so a second run streaming
   * concurrently (a cron turn beside an interactive one) cannot be mistaken
   * for this one's answer.
   */
  readonly runId?: string;
}

export interface AgentState {
  readonly status: AgentConnectionStatus;
  readonly errorMessage: string | null;
  /**
   * True when the terminal has everything it needs to show (vs. the login
   * screen): a gateway URL, plus a token for token-only hosts
   * (`supportsPairingRole === false`). Drives the top-level auth gate. Distinct
   * from `status`, which is the live socket state — `isPaired` persists across
   * reconnects and only flips on (re)pair / disconnect.
   */
  readonly isPaired: boolean;
  /** Messages for the active session — hydrated from per-session storage on switch. */
  readonly messages: readonly ChatMessage[];
  readonly agentUrl: string | null;
  /** Ephemeral "agent is doing something" indicator (Thinking / Calling tool / …). */
  readonly processing: ProcessingState | null;
  /**
   * Sub-agent subtasks running under each chat on this connection, keyed by
   * session id, so the in-flight row can say "2 running subtasks" and a
   * background chat's subtasks survive a switch away from it.
   *
   * Derived from `sessions.changed` pushes for child sessions, keyed by the
   * session their `spawnedBy` resolves to — which also bounds the map to
   * chats this client knows. Empty when the gateway reports no child
   * activity. See `subagent-tasks` in the core package; entries age out after
   * `SUBTASK_STALE_MS` of silence, and the whole map is dropped on reconnect
   * and account change (after a drop, nothing here can verify what still
   * runs).
   */
  readonly runningSubtasks: RunningSubtasks;
  /** A generative-UI render that has taken over the full chat viewport, if any. */
  readonly fullTerminalUi: {
    readonly messageId: string;
    readonly ui: UiRender;
  } | null;
  /** All known sessions for the current account, persisted across reloads. */
  readonly sessions: readonly ChatSession[];
  /**
   * Lifecycle of the gateway's roster read, so the sidebar can tell "you have
   * no chats" from "we have not heard back yet":
   *  - `idle`        — no account, or no gateway URL to ask
   *  - `loading`     — hydrated from cache, waiting on the first roster read
   *  - `ready`       — a roster read has landed for this account
   *  - `unavailable` — the read failed; the cached list is all there is
   *
   * `sessions` is seeded from localStorage as a first-paint CACHE, so an empty
   * list means nothing until this resolves — a new device, or one whose
   * storage was cleared, has chats on the gateway and none on disk. Showing
   * the "no chats yet" hint in that window told the user their history was
   * gone. Only the first outcome per account moves it off `loading`: later
   * reads must not flash a placeholder over a sidebar that already has rows.
   */
  readonly rosterStatus: 'idle' | 'loading' | 'ready' | 'unavailable';
  /**
   * The chat whose messages are being read from the agent right now, and for
   * which this device has nothing cached to show meanwhile.
   *
   * Opening a chat this device has never read paints from an empty cache, and
   * an empty transcript is indistinguishable from a chat nobody has written
   * in — so the view showed its "New Chat" greeting for the second the read
   * took, on a conversation with months of history in it. Null once the read
   * settles either way, and null for a chat that genuinely has no messages.
   */
  readonly loadingTranscriptFor: string | null;
  /**
   * The chat whose read FAILED, leaving this device with nothing to show for
   * it.
   *
   * The third answer the view needs. "Loading" and "empty" were the only two,
   * so a read that failed on a chat with no local copy landed back on the
   * "New Chat" greeting — telling the user their conversation is empty
   * because we could not reach the agent. Distinguishing the two is what lets
   * the view offer a retry instead of a false claim.
   *
   * Cleared when a read for that chat starts again or succeeds, so it never
   * outlives the condition it describes.
   */
  readonly failedTranscriptFor: string | null;
  /** User-created folders for grouping sessions. */
  readonly customFolders: readonly CustomFolder[];
  /** The session currently shown in the Chat tab. */
  readonly activeSessionId: string | null;
  /** Run ids the user explicitly cancelled via Stop (post-stop frames are dropped). */
  readonly cancelledRunIds: ReadonlySet<string>;
  /**
   * Models offered by the picker. Sourced ONLY from the gateway's
   * `/api/models` — there is deliberately no built-in fallback list, so this
   * is empty until the live catalog lands (see `modelsStatus`).
   */
  readonly models: readonly ModelInfo[];
  /**
   * Lifecycle of the live catalog fetch, so the composer can hold the picker's
   * slot with an honest placeholder instead of popping in:
   *  - `idle`        — not fetched yet (not authenticated / no account)
   *  - `loading`     — request in flight, or waiting on a retry
   *  - `ready`       — `models` is the gateway's catalog
   *  - `unavailable` — retries exhausted; the gateway never answered
   */
  readonly modelsStatus: 'idle' | 'loading' | 'ready' | 'unavailable';
  /**
   * Model of the ACTIVE session, as reported by the gateway (its session
   * `modelOverride` is the single source of truth — nothing is persisted
   * UI-side). Read silently via `sessions.describe` on hydrate / session
   * switch / reconnect; flipped optimistically on a picker pick and then
   * verified against the gateway (bounced back on mismatch). Fresh chats are
   * seeded with the last-used model (gateway default when there is no
   * previous session). `null` ⇒ not yet known (before the first read-back
   * resolves).
   */
  readonly selectedModelId: string | null;
  /**
   * Per-session model, keyed by session id — the UI-side truth for "what will
   * this chat run", mirroring each session's gateway `modelOverride`. In
   * memory only; a reload rebuilds it from the gateway.
   *
   * `selectedModelId` above is DERIVED from this (see the provider's
   * precedence chain) rather than stored independently. That is deliberate:
   * when the two were separate fields, every bug in this area was a call site
   * that updated one and not the other, or guarded the update on the wrong
   * thing. A late read-back now writes into its own session's slot, where it
   * is harmless, instead of racing a single shared scalar.
   */
  readonly sessionModels: Readonly<Record<string, string>>;
  /**
   * A pick made while no session exists yet (fresh install: the picker is
   * live but `activeSessionId` is null, so there is no slot to write). Ranks
   * ABOVE any resolved default in the derivation, which is what stops a
   * read-back from silently reverting it. Cleared once `sendMessage` mints the
   * session and seeds it.
   */
  readonly pendingModelId: string | null;
  /**
   * The gateway MAIN session's model, used only to show something sensible
   * before any chat exists. Display-only and ranked below `pendingModelId`:
   * it is the agent's shared session, so another surface (CLI, cron) may have
   * set it, and it must never become what a real chat runs.
   */
  readonly mainSessionModelId: string | null;
  /** The gateway's default model (`sessions.list` → `defaults`). */
  readonly gatewayDefaultModelId: string | null;
  /** Transient error surfaced briefly then auto-dismissed (e.g. "gateway unreachable"). */
  readonly transientError: string | null;
  /**
   * Run failures, keyed by the session that OWNS the failed run. Unlike
   * `transientError` these do NOT auto-dismiss: a run that fails while the
   * user is reading another chat (or looking away) must still be discoverable
   * when they come back, so the entry survives session switches and clears
   * only when the user dismisses it or retries that chat.
   *
   * Deliberately NOT part of the transcript: infrastructure failures are not
   * conversation content and must not persist to localStorage or sync as
   * messages. In-memory only — a reload starts clean.
   */
  readonly sessionErrors: Readonly<Record<string, string>>;
  /**
   * Context-window usage for the ACTIVE session, as reported by the gateway —
   * seeded with `sessions.describe` on connect / session switch and then kept
   * live by `sessions.changed` pushes.
   *
   * `null` means the gateway has not reported a usable reading yet (a chat
   * with no runs, a gateway that predates the fields). The meter renders
   * nothing rather than guessing, so the footer never shows a fabricated
   * percentage.
   */
  readonly contextUsage: SessionContextUsage | null;
  /**
   * Cross-device sync health, from the sync client's status callback. `null`
   * until the client reports (or when there is no sync client at all — the
   * local-only case), so a host without a sidecar renders nothing.
   *
   * Only `lastError` and `droppedSessionIds` are adopted into state — the
   * pending count changes on every queued write and would re-render the
   * whole tree during streaming for a number nothing displays. See
   * `ChatView`'s sync banner for what the user is told.
   */
  readonly syncStatus: Pick<
    TerminalSyncStatus,
    'lastError' | 'droppedSessionIds'
  > | null;
  /**
   * Whether the ACTIVE chat has older messages the agent holds but we have
   * not read yet, and whether a read for them is in flight.
   *
   * `chat.history` answers one bounded page — capped by `limit`, by a 6 MB
   * response budget that discards oldest-first, and by which transcript
   * branch is current. A page is therefore never proof of a conversation's
   * beginning, which is what this exists to say: `canLoadOlder` is the
   * gateway's own `hasMore`, so the UI can offer to fetch further back
   * instead of presenting a truncated chat as a whole one.
   */
  readonly canLoadOlder: boolean;
  readonly loadingOlder: boolean;
}

export interface AgentActions {
  /**
   * Send a chat message. Optional `context` is per-message hidden context
   * appended to the gateway payload (like `systemPreamble`, but per-call) —
   * it does NOT appear in the chat bubble. Used e.g. to attach a `/analyze`d
   * trade's structured data without cluttering the visible transcript.
   * Optional `attachments` are image attachments (base64 `content` +
   * metadata); the payload is sent to the gateway, the transcript keeps only
   * metadata + thumbnail.
   */
  readonly sendMessage: (
    content: string,
    opts?: {
      readonly context?: string;
      readonly attachments?: readonly ChatAttachment[];
    },
  ) => void;
  /**
   * Open (or reopen) the gateway connection. An optional `token` is a fresh
   * pairing token (e.g. from the setup form): it's persisted to the token
   * store — replacing any stale device token — before the handshake, so the
   * connect frame carries it as `auth.token`.
   */
  readonly connect: (url?: string, token?: string) => void;
  readonly disconnect: () => void;
  /** Clear messages in the *active* session only. */
  readonly clearMessages: () => void;
  /**
   * Fetch the page of older messages before the ones on screen and prepend
   * them. Safe to call freely: it is a no-op unless `canLoadOlder` is true
   * and nothing is already in flight.
   */
  readonly loadOlderMessages: () => void;
  /**
   * Read the open chat's messages from the agent again.
   *
   * For the case where the first read failed and left the chat with nothing
   * to show: without this the user's only recourse is to switch away and
   * back, or reload the tab, on a conversation the app is currently claiming
   * is empty.
   */
  readonly retryTranscriptRead: () => void;
  readonly respondToApproval: (approvalId: string, approved: boolean) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  /**
   * Call the gateway pod's sidecar over HTTP — the same route and credential
   * the model catalog and session sync use (`config.sidecar` forward when the
   * host set one, else the origin derived from the ws URL with the stored
   * gateway token). Resolves with the parsed JSON of a 2xx; rejects with a
   * `SidecarError` carrying the HTTP status (0 when nothing was exchanged)
   * and the sidecar's error code. See `sidecar/request.ts`.
   */
  readonly sidecarRequest: SidecarRequest;
  readonly requestPairing: () => Promise<string>;
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
  readonly dismissFullTerminalUi: () => void;
  /**
   * Clear the recorded run failure for a session (defaults to the active one).
   * The user acknowledging the error is what retires it — there is no timer.
   */
  readonly dismissSessionError: (sessionId?: string) => void;
  readonly createSession: (opts: {
    readonly folder: SessionFolder;
    readonly isTemporary?: boolean;
  }) => string;
  readonly switchSession: (sessionId: string) => void;
  readonly renameSession: (sessionId: string, title: string) => void;
  readonly pinSession: (sessionId: string, pinned: boolean) => void;
  readonly deleteSession: (sessionId: string) => void;
  readonly setSessionFolder: (
    sessionId: string,
    customFolderId: string | undefined,
  ) => void;
  readonly createCustomFolder: (name: string) => string;
  readonly renameCustomFolder: (folderId: string, name: string) => void;
  readonly pinCustomFolder: (folderId: string, pinned: boolean) => void;
  readonly deleteCustomFolder: (folderId: string) => void;
  readonly stopProcessing: () => void;
  readonly setSelectedModel: (modelId: string) => void;
}

export const INITIAL_AGENT_STATE: AgentState = {
  status: 'disconnected',
  errorMessage: null,
  isPaired: false,
  messages: [],
  agentUrl: null,
  processing: null,
  runningSubtasks: NO_RUNNING_SUBTASKS,
  fullTerminalUi: null,
  sessions: [],
  rosterStatus: 'idle',
  loadingTranscriptFor: null,
  failedTranscriptFor: null,
  customFolders: [],
  activeSessionId: null,
  cancelledRunIds: new Set<string>(),
  models: [],
  modelsStatus: 'idle',
  selectedModelId: null,
  transientError: null,
  sessionErrors: {},
  sessionModels: {},
  pendingModelId: null,
  mainSessionModelId: null,
  gatewayDefaultModelId: null,
  contextUsage: null,
  syncStatus: null,
  canLoadOlder: false,
  loadingOlder: false,
};

export const [useAgentState, AgentStateProvider] =
  createSafeContext<AgentState>('AgentState');

export const [useAgentActions, AgentActionsProvider] =
  createSafeContext<AgentActions>('AgentActions');

/**
 * Narrow, host-facing agent API — the ONLY agent surface re-exported from the
 * package's public entry (`useTerminalAgent`). Host `agentEffects` (see
 * `TerminalProps.agentEffects`) use it to drive the agent from host state:
 * e.g. a block-trade context pump calling `request(...)`, or a pending-message
 * drain calling `sendMessage(...)` once `status === 'authenticated'`.
 *
 * The full `AgentActions` (28 mutating methods) and `AgentState` stay internal
 * so they don't become a published semver contract.
 */
export interface TerminalAgentApi {
  /** Current connection/auth status — gate effects on `'authenticated'`. */
  readonly status: AgentConnectionStatus;
  /**
   * Live transcript of the ACTIVE session, in order. Read-only: lets a host
   * node react to agent replies it solicited via `sendMessage` (e.g. Paradex's
   * subkey setup flow prompts the agent to answer with a sentinel-keyed JSON
   * block, then scans assistant messages for it). A message that is still
   * streaming carries `isStreaming: true` and updates in place — wait for it
   * to flip off before treating the content as final.
   */
  readonly messages: readonly ChatMessage[];
  /**
   * Send a chat message as the user. The host preamble (if any) is applied,
   * and optional `context` is per-message hidden context appended to the
   * gateway payload only — not shown in the chat bubble. Use it to attach
   * structured data (e.g. a `/analyze`d trade) without cluttering the chat.
   * Optional `attachments` are image attachments (see `ChatAttachment`).
   */
  readonly sendMessage: (
    content: string,
    opts?: {
      readonly context?: string;
      readonly attachments?: readonly ChatAttachment[];
    },
  ) => void;
  /** Issue a raw gateway RPC (e.g. `set_block_trade_context`). */
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
}

export function useTerminalAgent(): TerminalAgentApi {
  const { sendMessage, request } = useAgentActions();
  const { status, messages } = useAgentState();
  return { status, messages, sendMessage, request };
}

/**
 * Host-facing AUTH seam — login state plus programmatic sign-in/out, so an
 * enclosing app can drive pairing itself instead of (or alongside) the
 * terminal's built-in login screen. The terminal owns its own credential
 * storage; this just lets the host push credentials in and read the result.
 *
 * Two ways a host signs the terminal in:
 *  - Declarative: pass `gatewayUrl` + `fallbackPairingToken` (or `resolveUrl`)
 *    in `TerminalConfig`; the terminal auto-pairs from them on mount.
 *  - Imperative: render a node in `agentEffects` that fetches credentials
 *    (e.g. after the host's own OAuth) and calls `login(url, token)`.
 *
 * `isLoggedIn` mirrors the internal pairing state; `logout()` unpairs (drops
 * the stored token) and returns to the login screen.
 */
export interface TerminalAuthApi {
  readonly isLoggedIn: boolean;
  readonly login: (url: string, token?: string) => void;
  readonly logout: () => void;
}

export function useTerminalAuth(): TerminalAuthApi {
  const { connect, disconnect } = useAgentActions();
  const { isPaired } = useAgentState();
  return {
    isLoggedIn: isPaired,
    login: (url: string, token?: string) => connect(url, token),
    logout: () => disconnect(),
  };
}
